import { cn } from '../../utils/cn'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'

/** "Daniel Husar" -> "DH". Falls back to one letter for a single-word name. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  return words
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('')
}

interface UserAvatarProps {
  /** Full name, used for the initials fallback and the image's alt text. */
  name: string
  /**
   * The person's picture — the uploaded headshot, else their legacy avatar.
   * Serialised as `avatar` on every user-shaped payload. Empty/undefined
   * renders initials instead.
   */
  src?: string | null
  className?: string
  fallbackClassName?: string
}

/**
 * The one way to picture a person in the app. Show it anywhere a user's name
 * appears — activity authors, assignment pickers, staff lists — so an uploaded
 * headshot (Settings → Your photo) follows them everywhere.
 *
 * Sized `size-8` like the underlying Avatar; pass `className` for anything else.
 */
export function UserAvatar({ name, src, className, fallbackClassName }: UserAvatarProps) {
  return (
    <Avatar className={cn('bg-ink-100 shrink-0', className)}>
      {src && <AvatarImage src={src} alt={name} className="object-cover" />}
      <AvatarFallback className={cn('text-ink-700 text-xs font-semibold', fallbackClassName)}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  )
}
